// `instruments` and `live` lead to the board for the boat's mode (TOM-358,
// boards-v3.md "System boards", ADR 0306 §3): At anchor, Underway or At dock,
// and At anchor only while the mode is unknown. The query string goes along,
// so a deep link such as `?anchor=controls` or `?prompt=anchor` still lands.
// A boat with no system boards yet has reported nothing to make them from: the
// page says it is waiting, and moves on by itself when the generator's
// `board_changed` frame brings the boards into the shell's list.

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useBoatChrome } from '../components/app/boatChrome';
import { boardPath, boatPath } from '../components/app/shellNav';
import { redirectKey, systemBoardFor } from '../lib/modeBoards';

/** How long to wait for the boat's mode before taking it as unknown. */
const MODE_WAIT_MS = 2_500;

export function ModeBoardRedirect() {
  const { boatId, boat, boards, archetype } = useBoatChrome();
  const location = useLocation();
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setWaited(true), MODE_WAIT_MS);
    return () => window.clearTimeout(id);
  }, []);

  const listed = boards.state.kind === 'ready' || boards.state.kind === 'error';
  if (!listed || (archetype === undefined && !waited)) {
    return (
      <section className="board-page" aria-busy="true">
        <p className="muted">Loading your boards…</p>
      </section>
    );
  }
  const systemBoards = boards.state.kind === 'ready' ? boards.state.systemBoards : [];
  const board =
    systemBoardFor(systemBoards, redirectKey(archetype ?? null)) ??
    systemBoardFor(systemBoards, 'anchored') ??
    systemBoards[0] ??
    null;
  if (board === null) {
    const name = boat?.display_name ?? 'your boat';
    return (
      <section className="board-page" data-testid="boards-waiting">
        <div className="tb-card board-empty">
          <div className="tb-empty">
            <h3 className="tb-empty__title">Setting up {name}’s boards</h3>
            <p className="tb-subhead">
              Waiting for the first readings from {name}. Its system boards appear as soon as it
              reports.
            </p>
          </div>
        </div>
      </section>
    );
  }
  const to = boatPath(boatId, boardPath(board.slug));
  return <Navigate to={`${to}${location.search}`} state={location.state} replace />;
}
